import {
 useCallback,
 useEffect,
 useRef,
 useState,
} from 'react'
import {IFormField} from 'models/Common'

type Fetcher<T> = (
 page: number,
 pageSize: number,
 query?: IFormField,
 signal?: AbortSignal
) => Promise<{data: T[]; total: number}>

type Props<T> = {
 fetcher: Fetcher<T>
 initialPageSize?: number
 enableSearch?: boolean
 enableFilters?: boolean
 setLoader?: (loading: boolean) => void
}

export function useListController<T>({
 fetcher,
 initialPageSize = 25,
 enableSearch = true,
 enableFilters = true,
 setLoader,
}: Props<T>) {
 const [data, setData] = useState<T[]>([])
 const [total, setTotal] = useState(0)

 const [currentPage, setCurrentPage] = useState(1)

 const [itemsPerPage, setItemsPerPage] =
  useState(initialPageSize)

 const [searchTerm, setSearchTerm] = useState('')
 const [filters, setFilters] = useState<IFormField | null>(
  null
 )
 const [filterCount, setFilterCount] = useState(0)

 const abortRef = useRef<AbortController | null>(null)

 const setSearch = (value: string) => {
  setSearchTerm(value)
  setCurrentPage(1)
 }

 const loadData = useCallback(
  async (
   page = currentPage,
   size = itemsPerPage,
   showLoader = true
  ) => {
   abortRef.current?.abort()
   const controller = new AbortController()
   abortRef.current = controller

   if (showLoader) {
    setLoader?.(true)
   }

   try {
    let query: IFormField = {}

    if (enableSearch && searchTerm.trim()) {
     query.search = searchTerm.trim()
    }

    if (enableFilters && filters) {
     query = {...query, ...filters}
    }

    const res = await fetcher(
     page,
     size,
     Object.keys(query).length ? query : undefined,
     controller.signal
    )
    setData(res.data)
    setTotal(res.total)
   } finally {
    if (showLoader) {
     setLoader?.(false)
    }
   }
  },
  [fetcher, currentPage, itemsPerPage, searchTerm, filters]
 )
 const changeItemsPerPage = (size: number) => {
  setItemsPerPage(size)
  setCurrentPage(1)
 }

 useEffect(() => {
  void loadData(currentPage, itemsPerPage, true)
 }, [currentPage, itemsPerPage, searchTerm, filters])

 const applyFilters = (data: IFormField) => {
  setFilters(data)
  setCurrentPage(1)
  const count = Object.values(data ?? {}).filter(
   (v) => v !== undefined && v !== null && v !== ''
  ).length
  setFilterCount(count)
 }

 return {
  data,
  total,
  currentPage: currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage: changeItemsPerPage,
  setSearchTerm: setSearch,
  applyFilters,
  filterCount,
  reload: async () => {
   try {
    await loadData(currentPage, itemsPerPage, false)
   } catch (e) {
    console.error(e)
   }
  },
 }
}
